import React, {
  useState,
  useCallback,
  useContext,
  useRef,
  useMemo,
} from "react";
import {
  Grid,
  AppCard,
  AppState,
  DialogModal,
  Tabs,
  Card,
  Button,
  Icons,
  Checkbox,
} from "@pushpress/shared-components";
import { documentToHtmlString } from "@contentful/rich-text-html-renderer";
import { useParams, useHistory } from "react-router-dom";
import Dashboard from "../../../layouts/Dashboard";
import { installApp, uninstallApp } from "../../../redux/actions/appActions";
import { AppContext } from "../../../context/app";
import Settings from "./Settings";
import Marketing from "./Marketing";
import ContentLoading from "../../../components/ContentLoading";
import { isUserSuperuser } from "../../../utils/session";
import { createNewChatWithPrePopulatedMessage } from "../../../utils/Intercom";
import { checkGoToOlderSettingsPage, hasAppAccess } from "../utils";
import { fetchRequest } from "../../../utils/http";
import { toast } from "react-toastify";
import {
  useAppBySlug,
  useAppsSettingsAndInstallations,
} from "../../../apis/appStore.hooks";
import { defaultToastOptions } from "../../../utils/misc";

const styles = {
  confirmationModal: {
    width: "366px",
  },
  checkboxModal: {
    width: "100%",
  },
};

const AppPage = () => {
  const { slug } = useParams() as {
    slug: string;
  };
  const { client, user } = useContext(AppContext);
  const [isLoadingInstall, setIsLoadingInstall] = useState<boolean>(false);
  const { data: app, refetch: getApp, isLoading } = useAppBySlug(slug);
  const { data } = useAppsSettingsAndInstallations();
  const { installations: installedApps } = data || { installations: [] };

  const formRef = useRef<HTMLFormElement>();
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [showProOnlyModal, setShowProOnlyModal] = useState<boolean>(false);
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [agreement, setAgreement] = useState<boolean>(false);
  const history = useHistory();

  // Check if user can install if agreement is necessary
  const hasAgreementToInstall =
    (app?.modalAgreementCheckboxText && agreement) ||
    !app?.modalAgreementCheckboxText;

  // This mocks the older PHP Form to be submited,
  // but as an "API" because
  // I want to keep on the react code for the settings page
  const handleInstallUninstallFetchForm = useCallback(
    async (type: "install" | "uninstall") => {
      try {
        setIsLoadingInstall(true);
        const formData = new FormData(formRef.current);
        const body = Object.fromEntries(formData);

        // Fetch request with all Form data
        const res = await fetchRequest({
          method: formRef.current?.method,
          path: formRef.current?.action,
          body,
        });

        // This means that PHP token was expired and we should force a logout.
        if (res.redirected && res.url.includes("/login")) {
          window.location.href = "/account/logout";
        }

        const resApp = await getApp();

        if (resApp.data?.isInstalled && type === "uninstall") {
          // We are having a case where the PHP returns 200, but it really doesn't uninstall the app so I'm checking if the app was really deleted
          throw new Error("Error during uninstall, app keep installed");
        }
      } catch (err) {
        toast.error(
          `Whoops, an error happened during ${type}. Please contact customer support!`,
          defaultToastOptions,
        );
      } finally {
        setIsLoadingInstall(false);
      }
    },
    [formRef],
  );

  const handleInstallApp = useCallback(async () => {
    if (!hasAgreementToInstall || !app) {
      return;
    }

    if (app.customInstallButtonLinkForNonSuperUsers && !isUserSuperuser(user)) {
      window.open(app.customInstallButtonLinkForNonSuperUsers, "_blank");
    }

    if (app.onlySuperUserCanActivate && !isUserSuperuser(user)) {
      toast.info("Whoops, only super users can install this app!", {
        position: "bottom-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        progress: undefined,
      });
      return;
    }

    try {
      // Check if an app is legacy AND doesn't have a new settings page
      if (app.isLegacy && checkGoToOlderSettingsPage(app)) {
        // If true, submit the form
        formRef.current?.submit();
      } else if (app.isLegacy) {
        // If false but it's legacy, run the function who mocks the form submit
        handleInstallUninstallFetchForm("install");
      } else {
        // If it's not legacy, call the new API
        setIsLoadingInstall(true);
        await installApp(client.uuid, app.uuid);
        // If doesn't have a settings page, go to the older page
        if (checkGoToOlderSettingsPage(app)) {
          window.location.href = `/v2/integrations/app/${app.slug}`;
        } else {
          // I have a new settings page, just call getApp to get
          // app data with the settings after installation
          await getApp();
          setIsLoadingInstall(false);
        }
      }
    } catch (err) {
      toast.info(`Whoops, an error happened during the installation!`, {
        position: "bottom-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        progress: undefined,
      });
      console.log(err);
    }
  }, [app, handleInstallUninstallFetchForm, agreement]);

  const handleClickInstallApp = useCallback(() => {
    if (!app) return;

    if (!hasAppAccess(app, client)) {
      setShowProOnlyModal(true);
    } else if (app.showModalConfirmation && !app.beta) {
      setShowConfirmationModal(true);
    } else {
      handleInstallApp();
    }
  }, [app, client, setShowConfirmationModal, setShowProOnlyModal]);

  const handleUninstallApp = useCallback(async () => {
    if (!app) return;

    // If is super user and doesn't request deactivate via chat
    if (
      !app.deactivateViaChat &&
      (isUserSuperuser(user) || !app.onlySuperUserCanDeactivate)
    ) {
      // Check if an app is legacy AND doesn't have a new settings page
      if (app.isLegacy && checkGoToOlderSettingsPage(app)) {
        // If true, submit the form
        formRef.current?.submit();
      } else if (app.isLegacy) {
        // If false but it's legacy, run the function who mocks the form submit
        handleInstallUninstallFetchForm("uninstall");
      } else {
        // If it's not legacy, call the new API
        const installation = installedApps.find((ia) => ia.appId === app.uuid);
        setIsLoadingInstall(true);

        await uninstallApp(client.uuid, installation?.id);

        // If doesn't have a settings page, go to the older page
        if (checkGoToOlderSettingsPage(app)) {
          window.location.href = `/v2/integrations/app/${app.slug}`;
        } else {
          // I have a new settings page, just call getApp to get
          // app data with the settings after installation
          await getApp();
          setIsLoadingInstall(false);
        }
      }
    } else {
      createNewChatWithPrePopulatedMessage(
        `I would like to deactivate ${app?.name}.`,
      );
    }
  }, [
    app,
    client,
    setShowConfirmationModal,
    setShowProOnlyModal,
    installedApps,
  ]);

  const getAppState = useCallback(() => {
    if (app && !hasAppAccess(app, client)) {
      return AppState.ProOnly;
    } else if (app?.beta) {
      return AppState.WaitList;
    } else {
      return AppState.ReadyToSale;
    }
  }, [app]);

  const modalContent = useMemo(() => {
    if (app?.modalConfirmationContent) {
      return (
        <>
          <div
            style={styles.confirmationModal}
            dangerouslySetInnerHTML={{
              __html: documentToHtmlString(app.modalConfirmationContent as any),
            }}
          />
          {app.modalAgreementCheckboxText && (
            <Grid container>
              <Grid item xs={1}>
                <Checkbox
                  checked={agreement}
                  onToggle={() => {
                    setAgreement((prev) => !prev);
                  }}
                />
              </Grid>
              <Grid item xs={11}>
                <div
                  style={styles.checkboxModal}
                  dangerouslySetInnerHTML={{
                    __html: documentToHtmlString(
                      app.modalAgreementCheckboxText as any,
                    ),
                  }}
                />
              </Grid>
            </Grid>
          )}
        </>
      );
    }

    return (
      <h2>
        You are installing <b>{app?.name}</b>
      </h2>
    );
  }, [app, setAgreement, agreement]);

  if (!app || isLoading || isLoadingInstall) {
    return <ContentLoading />;
  }

  if (app.isInstalled) {
    return (
      <Dashboard>
        {/* This is the form we use to uninstall or request beta for some LEGACY app */}
        <form
          action={`/integrations/app/${app.slug}`}
          method="post"
          ref={formRef}
        >
          <input type="hidden" name="application_id" value={app.uuid} />
          <input type="hidden" name="setting[enabled]" value={0} />
        </form>
        <Button
          href="/app-store"
          onClick={() => {
            history.push("/app-store");
          }}
          buttonType="text"
          text="Back to My Apps"
          icon={Icons.ArrowLeft}
          iconLocation="left"
        />
        <Grid container spacing={3} marginY={3}>
          <Grid xs={12} sm={4} lg={3} item>
            <AppCard
              appData={{
                name: app.name,
                state: getAppState(),
                price: String(app.price),
                setupFee: String(app.setupFee),
                logoUrl: app.image.fields.file.url,
                installed: app.isInstalled,
                betaRegistered: !!app.betaRegistered,
              }}
              onClick={handleUninstallApp}
            />
          </Grid>
          <Grid xs={12} sm={8} lg={9} item>
            <Card>
              <Tabs
                tabHeaders={["Settings", "About"]}
                selectedIndex={selectedTabIndex}
                changeTab={setSelectedTabIndex}
                onTabClick={setSelectedTabIndex}
              >
                <Settings app={app} />
                <Marketing app={app} />
              </Tabs>
            </Card>
          </Grid>
        </Grid>
      </Dashboard>
    );
  }

  return (
    <>
      <DialogModal
        title=""
        visible={showConfirmationModal}
        closeModal={() => setShowConfirmationModal(false)}
        primaryBtnTitle={"OK"}
        secondaryBtnTitle={"Cancel"}
        primaryButtonOptions={{
          icon: Icons.ArrowRight,
          iconLocation: "right",
          disabled: !hasAgreementToInstall,
        }}
        primaryHandler={handleInstallApp}
        secondaryHandler={() => setShowConfirmationModal(false)}
      >
        {modalContent}
      </DialogModal>
      <DialogModal
        title=""
        visible={showProOnlyModal}
        closeModal={() => setShowProOnlyModal(false)}
        primaryBtnTitle={"Upgrade Options"}
        secondaryBtnTitle={"Cancel"}
        primaryHandler={() => {
          window.location.href = "/settings/account";
        }}
        secondaryHandler={() => setShowProOnlyModal(false)}
      >
        <h2>Requires Pro or Max Plan</h2>
        <p>
          This feature is available when you subscribe to the PushPress Pro or
          Max Plan.
        </p>
      </DialogModal>
      <Dashboard>
        {/* This is the form we use to install or request beta for some LEGACY app */}
        <form
          action={
            app.beta
              ? `/integrations/beta_request/${app.slug}`
              : `/integrations/app/${app.slug}`
          }
          method="post"
          ref={formRef}
        >
          {app.beta ? (
            <>
              <input type="hidden" name="client_id" value={client.uuid} />
              <input type="hidden" name="user_id" value={user?.uuid} />
            </>
          ) : (
            <>
              <input type="hidden" name="application_id" value={app.uuid} />
              <input type="hidden" name="setting[enabled]" value={1} />
            </>
          )}
        </form>
        <Button
          href="/app-store"
          onClick={() => {
            history.push("/app-store");
          }}
          buttonType="text"
          text="Back to Discover Apps"
          icon={Icons.ArrowLeft}
          iconLocation="left"
        />
        <Grid container spacing={3} marginY={3}>
          <Grid xs={12} sm={4} lg={3} item>
            <AppCard
              appData={{
                name: app.name,
                state: getAppState(),
                price: String(app.price),
                setupFee: String(app.setupFee),
                logoUrl: app.image.fields.file.url,
                installed: !!app.isInstalled,
                betaRegistered: !!app.betaRegistered,
                customButtonText:
                  !isUserSuperuser(user) &&
                  app.customInstallButtonForNonSuperUsers,
              }}
              onClick={handleClickInstallApp}
            />
          </Grid>
          <Grid xs={12} sm={8} lg={9} item>
            <Card>
              <Marketing app={app} onJoinWaitlist={handleClickInstallApp} />
            </Card>
          </Grid>
        </Grid>
      </Dashboard>
    </>
  );
};

export default AppPage;
